import React, { useState, useEffect } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, CircularProgress, Chip,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import { Forms, WEBSITE_INQUIRY_TYPE } from "../../api/requests/Forms";
import ListAltIcon from "@mui/icons-material/ListAlt";
import WebsiteInquiries from "./WebsiteInquiries";

const formTypes = [
  { value: WEBSITE_INQUIRY_TYPE, label: "Website Enquiries" },
  { value: "hospital_cash", label: "Hospital Cash" },
  { value: "business_loan", label: "Business Loan" },
  { value: "home_loan", label: "Home Loan" },
  { value: "personal_loan", label: "Personal Loan" },
  { value: "ca_registration", label: "CA Registration" },
];

const FormResponses = () => {
  const [selectedTab, setSelectedTab] = useState(formTypes[0].value);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    if (selectedTab === WEBSITE_INQUIRY_TYPE) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await Forms.getForms({
          page: page + 1,
          limit: rowsPerPage,
          type: selectedTab,
        });
        setData(response?.data || []);
        setTotalRows(Number(response?.pagination?.total) || 0);
      } catch (err) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedTab, page, rowsPerPage]);

  const flattenObject = (obj, prefix = "") => {
    let result = {};
    for (const key in obj) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        result = { ...result, ...flattenObject(obj[key], prefix + key + ".") };
      } else {
        result[prefix + key] = obj[key];
      }
    }
    return result;
  };

  const flattenedData = data?.map((item) => flattenObject(item));

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ListAltIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Form Responses
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            Enquiries from the website and submitted forms across all categories.
          </Typography>
        </Box>

        {/* Tab Chips */}
        <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
          {formTypes.map((type) => (
            <Chip
              key={type.value}
              label={type.label}
              clickable
              onClick={() => { setSelectedTab(type.value); setPage(0); }}
              sx={{
                fontWeight: 600, fontSize: "12px", borderRadius: "8px",
                height: 32, px: 0.5,
                backgroundColor: selectedTab === type.value ? Colors.primary : Colors.white,
                color: selectedTab === type.value ? Colors.white : Colors.primaryTextColor,
                border: `1px solid ${selectedTab === type.value ? Colors.primary : Colors.dividerColor}`,
                "&:hover": {
                  backgroundColor: selectedTab === type.value ? Colors.hoverColorBtn : Colors.blueSoft,
                },
              }}
            />
          ))}
        </Box>

        {/* Content */}
        {selectedTab === WEBSITE_INQUIRY_TYPE ? <WebsiteInquiries /> : (
        <Box sx={{
          backgroundColor: Colors.white, borderRadius: "12px",
          border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
        }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={36} sx={{ color: Colors.primary }} />
            </Box>
          ) : error ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: Colors.error, fontSize: "14px" }}>{error}</Typography>
            </Box>
          ) : flattenedData.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <ListAltIcon sx={{ fontSize: 40, color: Colors.dividerColor, mb: 1 }} />
              <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px" }}>
                No form responses found for this category
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {Object.keys(flattenedData[0]).map((key) => (
                        <TableCell key={key} sx={{
                          fontWeight: 600, fontSize: "11px", color: Colors.secondaryColor,
                          textTransform: "uppercase", letterSpacing: "0.5px", py: 1.5,
                          backgroundColor: "#F9FBFC",
                          borderBottom: `1px solid ${Colors.dividerColor}`,
                          whiteSpace: "nowrap",
                        }}>
                          {key.replace(/_/g, " ").replace(/\./g, " ")}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {flattenedData.map((form, idx) => (
                      <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#FAFBFF" }, transition: "background 0.15s" }}>
                        {Object.values(form).map((value, i) => (
                          <TableCell key={i} sx={{
                            fontSize: "12px", fontWeight: 400, color: Colors.primaryTextColor,
                            borderBottom: `1px solid ${Colors.dividerColor}`,
                            maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {value}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  borderTop: `1px solid ${Colors.dividerColor}`,
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "12px", color: Colors.secondaryColor,
                  },
                }}
              />
            </>
          )}
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default FormResponses;
